import { isValidElement, type ReactNode } from 'react'
import type { Block } from '@w/content-blocks'
import type { CodeToken } from '@w/code-highlighting'
import { editorCodeRuns } from '../../../../../whiteboard/src/editor-code-runs'
import { codeSideText } from '../../review-diff-code'
import { plain } from '../../core/tree-read'
import type { BlockView, CoreViewApi } from '../view-api'
import { CodeFrame, CodeView } from '../blocks/presentation'
import { CommentView, diffClass } from './views'

const toneClass = { error: 'text-error', warning: 'text-warning', info: 'text-info', success: 'text-success', primary: 'text-primary', muted: 'text-on-surface-secondary' }

export const ReviewCodeView: BlockView = props => props.api.mode === 'review' && props.block.type === 'pre' && !props.block.latex
  ? <ReviewCode block={props.block} api={props.api} /> : <CodeView {...props} />

/** Tokenize separate source streams, then slice tokens back into the existing diff tree. */
export function ReviewCode({ block, api }: { block: Extract<Block, { type: 'pre' }>; api: CoreViewApi }) {
  const lines = block.children.every(child => child.type === 'diff') ? block.children : [block]
  const source = lines.filter(line => line.type !== 'diff' || line.kind !== 'delete').map(line => codeSideText(line, 'new')).join('\n')
  // Whole removed diagrams are still useful previews; inline deletions never leak into a proposed preview.
  const previewSource = source || lines.filter(line => line.type !== 'diff' || line.kind !== 'add').map(line => codeSideText(line, 'old')).join('\n')
  return <CodeFrame block={block} api={api} source={previewSource}>{lines.map(line => renderLine(line, api, {
    old: editorCodeRuns(codeSideText(line, 'old'), block.lang ?? 'text'),
    next: editorCodeRuns(codeSideText(line, 'new'), block.lang ?? 'text'),
  }))}</CodeFrame>
}

function renderLine(line: Block, api: CoreViewApi, tokens?: { old: CodeToken[]; next: CodeToken[] }): ReactNode {
  const old = { tokens: tokens?.old ?? [{ text: codeSideText(line, 'old') }], index: 0, offset: 0 }
  const next = { tokens: tokens?.next ?? [{ text: codeSideText(line, 'new') }], index: 0, offset: 0 }
  function consume(stream: typeof old, count: number, render: boolean): ReactNode[] {
    const result: ReactNode[] = []
    while (count > 0 && stream.index < stream.tokens.length) {
      const token = stream.tokens[stream.index]
      const text = token.text.slice(stream.offset, stream.offset + count)
      if (text && render) {
        let position = stream.offset
        const content = text.split('\n').map(part => {
          const start = position
          position += part.length + 1
          return <span key={`${stream.index}:${start}`}>{start > stream.offset && <br />}{part}</span>
        })
        result.push(<span key={`${stream.index}:${stream.offset}`} className={token.tone ? toneClass[token.tone] : undefined}>{content}</span>)
      }
      count -= text.length
      stream.offset += text.length
      if (stream.offset === token.text.length) { stream.index++; stream.offset = 0 }
    }
    return result
  }
  function walk(block: Block, kind: 'add' | 'delete' | 'context', top = false): ReactNode {
    if (block.type === 'diff' && block.kind !== 'context') kind = block.kind
    if ('children' in block) {
      const children = (block.children ?? []).map(child => walk(child, kind))
      if (block.type === 'comment') return <CommentView key={block.id} block={block} api={api}>{children}</CommentView>
      if (block.type !== 'diff') return children
      const Tag = top ? 'div' : 'span'
      return <Tag key={block.id} data-block-id={block.id} data-diff-kind={block.kind} className={diffClass(block.kind, !top)}>{children}</Tag>
    }
    const count = plain(block).length
    const before = kind === 'add' ? [] : consume(old, count, kind === 'delete')
    const after = kind === 'delete' ? [] : consume(next, count, true)
    const pieces = [...before, ...after]
    const only = pieces.length === 1 && isValidElement<{ className?: string }>(pieces[0]) ? pieces[0] : undefined
    return <span key={block.id} data-block-id={block.id} data-leaf="true" className={only?.props.className}>{pieces}</span>
  }
  return walk(line, 'context', true)
}
